import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import * as Sentry from '@sentry/vue'
import App from './App.vue'
import router from './router/index.js'
import auth from './plugins/auth'
import { registerDependencies } from './register-dependencies-helper'
import Element from './plugins/element.js'
import Axios from './plugins/axios'
import VideoPlayer from './plugins/video-player'
import i18n from './i18n'
import Permissions from '@shared/plugins/permissions'
import FeatureFlags from '@shared/plugins/feature-flags'
import { useAuthStore } from '@shared/store/auth'
import './assets/tailwind.css'
import 'element-plus/dist/index.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import 'vue-universal-modal/dist/index.css'
import './styles/main.css'
import 'v-calendar/dist/style.css'
import { vueKeycloak } from '@josempgon/vue-keycloak'
import { isUnifiedCommerce, isKeycloackEnabled } from '@shared/constants'
import { env } from '@shared/config/env'
import emitter from 'tiny-emitter/instance'
import { getLastUpdatedDate } from './utils/date/index'
import {
  discardForeignKeycloakSession,
  getKeycloakConfig
} from './plugins/keycloak'
import { captureSupportUserCredentials } from './store/auth'
import { piniaPluginGlobalReset } from './store/plugins/global-reset'
import { piniaPluginLogException } from './store/plugins/log-exception'
import { VueKonva, config as vueKonvaConfig } from './plugins/vue-konva'
import { beforeSend } from './config/sentry-filter'
import {
  getVitePreloadErrorFromEvent,
  handleStaleAssetFailure
} from './utils/stale-asset-recovery'
import {
  showBootstrapLoading,
  removeBootstrapLoading
} from './utils/bootstrap-loading'

if (isUnifiedCommerce()) {
  showBootstrapLoading()
}

const isProd = env.isProduction
const buildTime = env.buildDate

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(piniaPluginGlobalReset).use(piniaPluginLogException)

export const app = createApp(App)

if (env.sentryDsn) {
  Sentry.init({
    debug: false,
    app,
    dsn: env.sentryDsn,
    release: env.sentryRelease,
    environment:
      env.appEnv === 'test'
        ? 'testing'
        : env.appEnv === 'prod'
          ? 'production'
          : 'integration',
    sampleRate: isProd ? 0.5 : 0.3,
    beforeSend,
    integrations: [Sentry.browserTracingIntegration()]
  })
}

if (!import.meta.env.DEV) {
  globalThis.addEventListener('vite:preloadError', (event) => {
    const err = getVitePreloadErrorFromEvent(event)
    if (!err) return
    event.preventDefault()
    const recovery = handleStaleAssetFailure(err, { trustedStaleFailure: true })
    if (recovery === 'report') {
      router.push({
        name: '500',
        params: { error: err }
      })
    }
  })
}

if (isUnifiedCommerce() && isKeycloackEnabled()) {
  // Both before the adapter installs: init reads storage and can navigate to
  // Keycloak straight away, taking the support agent's URL with it.
  discardForeignKeycloakSession()
  captureSupportUserCredentials()
  app.use(vueKeycloak, getKeycloakConfig())
}

app.config.productionTip = false
if (isProd) {
  app.config.devtools = true
  app.config.debug = false
  app.config.silent = true
}

app.config.globalProperties.$lastUpdated = getLastUpdatedDate(buildTime, isProd)
// Check if there is a better option than this emitter
app.config.globalProperties.$emitter = emitter

//Register permissions and featureFlags plugins
app.use(Permissions)
app.use(pinia)
app.use(FeatureFlags)
app.use(Element)
app.use(Axios)
app.use(VideoPlayer)

//Register components and plugins
//Must run before app.use(router): Vue Router's install triggers initial
//navigation synchronously, and route redirects can create pinia stores
//(e.g. useAuthStore in getParentRedirectTo). The pinia logException plugin
//reads $logException off globalProperties, which is set by ThLogException
//inside registerDependencies.
registerDependencies(app, { i18n, isProd })

app.use(i18n)
app.use(VueKonva, vueKonvaConfig)

// Set window.app before auth startup finishes. getMeData error handling reads
// globalProperties from window.app, so assigning it after the awaits would make
// startup /me failures return before logging or handling authentication errors.
window.app = app
;(async () => {
  const authStore = useAuthStore()

  try {
    await auth.getSession()

    if (authStore.isAuthenticated) {
      await authStore.waitForMeData()
    }
  } catch {
    // This flag means the startup auth check finished, not that auth succeeded.
    // Route guards wait for it before redirecting unauthenticated users.
    authStore.setInitialized(true)
  } finally {
    if (isUnifiedCommerce()) {
      removeBootstrapLoading()
    }
    app.use(router)
    app.mount('#app')
  }
})()
